import { useEffect, useRef, useState } from "react";

const benefits = [
  {
    number: 1,
    title: "Efficiency and cost savings",
    description:
      "Generate 100+ ad variations in minutes instead of days. Replace your paid media buyer, designer, and copywriter with AI that produces enterprise-level creative—saving you $5K-15K monthly in agency fees.",
  },
  {
    number: 2,
    title: "Scalability and diversity",
    description:
      "Test 10x more creative concepts than your competitors. While they're stuck waiting on designers, you're launching new campaigns, exploring fresh angles, and finding winning combinations faster.",
  },
  {
    number: 3,
    title: "Differentiation",
    description:
      "Your customers scroll past hundreds of ads daily. Stand out with product photography, lifestyle shots, and ad creative that's uniquely yours—not pulled from the same stock libraries as everyone else.",
  },
  {
    number: 4,
    title: "Endless possibilities",
    description:
      "Go beyond just \"faster ads.\" Optimize product pages, generate conversion-focused copy, track performance across channels, and automate the creative workflow that used to require an entire team.",
  },
];

const BenefitsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = itemRefs.current.findIndex((ref) => ref === entry.target);
            if (index !== -1) {
              setActiveIndex(index);
            }
          }
        });
      },
      {
        threshold: 0.6,
        rootMargin: "-20% 0px -40% 0px",
      }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Column - Heading */}
          <div className="lg:sticky lg:top-32">
            <p className="text-sm font-semibold tracking-widest text-gray-500 uppercase mb-4">
              Your AI Marketing Team
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl leading-tight text-foreground mb-6">
              Be more <em className="not-italic font-serif">creative</em>,{" "}
              <em className="not-italic font-serif">effective</em>,<br />
              and <em className="not-italic font-serif">productive</em>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
              Stop juggling agencies, freelancers, and marketing platforms. thinker gives you an entire marketing team that works 24/7—no hiring, no onboarding, no meetings.
            </p>
          </div>

          {/* Right Column - Numbered List */}
          <div className="relative">
            {/* Vertical connecting line */}
            <div className="absolute left-5 top-8 bottom-8 w-px bg-gray-300 overflow-hidden">
              {/* Animated progress line */}
              <div
                className="w-full bg-blue-600 transition-all duration-500 ease-out"
                style={{
                  height: `${((activeIndex + 1) / benefits.length) * 100}%`,
                }}
              />
            </div>

            <div className="space-y-12">
              {benefits.map((benefit, index) => (
                <div
                  key={benefit.number}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`relative flex gap-6 transition-opacity duration-300 ${
                    index <= activeIndex ? "opacity-100" : "opacity-40"
                  }`}
                >
                  {/* Number circle */}
                  <div
                    className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      index <= activeIndex
                        ? "border-blue-600 bg-white"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    <span
                      className={`text-sm font-medium transition-colors duration-300 ${
                        index <= activeIndex ? "text-blue-600" : "text-gray-500"
                      }`}
                    >
                      {benefit.number}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-1">
                    <h3
                      className={`text-xl font-semibold mb-2 transition-colors duration-300 ${
                        index <= activeIndex ? "text-foreground" : "text-gray-500"
                      }`}
                    >
                      {benefit.title}
                    </h3>
                    <p
                      className={`leading-relaxed transition-colors duration-300 ${
                        index <= activeIndex ? "text-muted-foreground" : "text-gray-500"
                      }`}
                    >
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
